import React from 'react';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    AutoComplete,
    DateShortRelative,
    Heading,
    Menu,
    MenuItem,
    MenuRoot,
    MenuSearch,
    MenuSection,
    MenuTrigger,
    Popover,
    Separator,
} from '@gitbook/spine-react';
import { useNow } from '@gitbook/util-react-hooks';

import { DEFAULT_CONVERSATION_LOCAL_ID } from '../Agent';
import { AgentDockButton } from './AgentDockButton';
import type { AgentDockTab } from './state';

const DAY_MS = 24 * 60 * 60 * 1000;

const UNTITLED_CONVERSATION_LABEL = 'New chat';

/** Recency buckets shown as sections in the history, most recent first. */
const RECENCY_GROUPS = [
    { key: 'today', label: 'Today' },
    { key: 'week', label: 'This week' },
    { key: 'before', label: 'Older' },
] as const;

type RecencyKey = (typeof RECENCY_GROUPS)[number]['key'];

function getConversationLabel(conversation: api.AgentConversation): string {
    return conversation.title?.trim() || UNTITLED_CONVERSATION_LABEL;
}

/**
 * When the conversation was started, which is both what each row reports and what sorts it into a
 * section. Deliberately not the last activity: replying to an old conversation would move it to
 * "Today" and renumber the list under the user, when what they are looking for is the chat they
 * remember having at some point.
 */
function getConversationTime(conversation: api.AgentConversation): number {
    return new Date(conversation.createdAt).getTime();
}

/**
 * Bucket conversations by recency (most recent first within each bucket).
 */
function groupConversationsByRecency(
    conversations: api.AgentConversation[],
    timestamp: number
): Record<RecencyKey, api.AgentConversation[]> {
    const now = new Date(timestamp);
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfWeek = startOfToday - 6 * DAY_MS;

    const groups: Record<RecencyKey, api.AgentConversation[]> = {
        today: [],
        week: [],
        before: [],
    };

    for (const conversation of conversations) {
        const time = getConversationTime(conversation);
        if (time >= startOfToday) {
            groups.today.push(conversation);
        } else if (time >= startOfWeek) {
            groups.week.push(conversation);
        } else {
            groups.before.push(conversation);
        }
    }

    return groups;
}

/**
 * How long ago the conversation was started, shown at the end of its row.
 *
 * Muted and shrink-proof: it is there to place a conversation in time at a glance, so it must not
 * compete with the title, and must not be the thing that truncates when the title is long.
 */
function ConversationAge(props: { conversation: api.AgentConversation }) {
    const { conversation } = props;

    return (
        <DateShortRelative
            value={new Date(getConversationTime(conversation))}
            className="shrink-0 text-muted"
        />
    );
}

/**
 * The conversations on offer in the history, and the way in to one of them.
 *
 * Fetched by the dock rather than in the menu: the menu's children are a react-aria collection,
 * which only understands `MenuSection`/`MenuItem` elements, so a section cannot be a component of
 * its own that fetches what it shows — doing that breaks the whole menu. Opening one is the
 * surface's own business too: the dock gives the conversation a tab of its own, while the anchored
 * panel replaces the conversation it is showing.
 */
export type AgentConversationHistory = {
    conversations: api.AgentConversation[];
    /**
     * Conversations of the older change-request agent on the change request being viewed. Produced
     * by background work — implementing a finding, reviewing, answering a comment thread — so they
     * are listed to be opened on demand, and never opened on the user's behalf. Empty off a change
     * request, where they mean nothing.
     */
    changeRequestConversations: api.AgentConversation[];
    changeRequest: { spaceId: string; changeRequestId: string } | null;
    onOpenConversation: (tab: AgentDockTab) => void;
};

/**
 * The conversation history.
 *
 * Opened from the dock's own button by default, since its tab row has a place for one. The anchored
 * panel has no such row, so it passes the title of the conversation it is showing instead.
 */
export function AgentHistoryMenu(props: {
    history: AgentConversationHistory;
    /** Control that opens the menu. */
    trigger?: React.ReactNode;
}) {
    const { history, trigger } = props;
    const { conversations, changeRequestConversations, changeRequest, onOpenConversation } =
        history;
    // The dock is never remounted by navigation, so a session outlasts a day. Taking the clock from
    // a hook that ticks keeps "Today" meaning today, rather than whenever the list last changed.
    const now = useNow(60 * 60 * 1000);
    const groups = React.useMemo(
        () => groupConversationsByRecency(conversations, now),
        [conversations, now]
    );
    const isEmpty = conversations.length === 0 && changeRequestConversations.length === 0;

    return (
        <MenuRoot>
            <MenuTrigger>
                {trigger ?? <AgentDockButton icon={Icon.History} label="Conversation history" />}
            </MenuTrigger>
            <Popover>
                {/* The filter is spelled out rather than left to Spine's hidden one, so it reads as
                    the menu's title and says what typing here does. Wrapping the menu in the
                    autocomplete is what replaces that hidden input with this one. */}
                <AutoComplete>
                    <MenuSearch label="Filter chat history" />
                    <Menu>
                        {isEmpty ? <MenuItem disabled>No conversations yet</MenuItem> : null}
                        {/* First, above the user's own conversations: they belong to the change
                            request on screen, so they are the ones being looked for while on it. */}
                        {changeRequest && changeRequestConversations.length > 0
                            ? [
                                  <MenuSection key="change-request">
                                      <Heading>On this change request</Heading>
                                      {changeRequestConversations.map((conversation) => (
                                          <MenuItem
                                              key={conversation.id}
                                              suffix={
                                                  <ConversationAge conversation={conversation} />
                                              }
                                              onAction={() =>
                                                  onOpenConversation({
                                                      kind: 'change_request',
                                                      spaceId: changeRequest.spaceId,
                                                      changeRequestId:
                                                          changeRequest.changeRequestId,
                                                      // The default conversation's durable object
                                                      // is addressed with no id at all; passing the
                                                      // sentinel its row is keyed by would open a
                                                      // different, empty object.
                                                      ...(conversation.id ===
                                                      DEFAULT_CONVERSATION_LOCAL_ID
                                                          ? {}
                                                          : { conversationId: conversation.id }),
                                                  })
                                              }
                                          >
                                              {getConversationLabel(conversation)}
                                          </MenuItem>
                                      ))}
                                  </MenuSection>,
                                  <Separator key="change-request-separator" />,
                              ]
                            : null}
                        {/* Ruled off from each other, so a heading reads as the start of its own
                            stretch of conversations rather than as a label floating between two. */}
                        {RECENCY_GROUPS.flatMap((group, index) => {
                            const items = groups[group.key];
                            if (items.length === 0) {
                                return [];
                            }
                            const section = (
                                <MenuSection key={group.key}>
                                    <Heading>{group.label}</Heading>
                                    {items.map((conversation) => (
                                        <MenuItem
                                            key={conversation.id}
                                            suffix={<ConversationAge conversation={conversation} />}
                                            onAction={() =>
                                                onOpenConversation({
                                                    kind: 'organization',
                                                    conversationId: conversation.id,
                                                })
                                            }
                                        >
                                            {getConversationLabel(conversation)}
                                        </MenuItem>
                                    ))}
                                </MenuSection>
                            );
                            // Between sections only: a rule under the last one would fence off the
                            // end of the menu.
                            const isLast = !RECENCY_GROUPS.slice(index + 1).some(
                                (next) => groups[next.key].length > 0
                            );
                            return isLast
                                ? [section]
                                : [section, <Separator key={`${group.key}-separator`} />];
                        })}
                    </Menu>
                </AutoComplete>
            </Popover>
        </MenuRoot>
    );
}
